import { useEffect, useMemo, useState, type FormEvent } from "react";

import { ApiError } from "@/pages/auth/api";
import { getStoredSessionToken } from "@/pages/auth/storage";
import { getOwnProfile } from "@/pages/profile/api";
import { useI18n } from "@/shared/i18n/LanguageContext";
import { formatDevlogClock, formatDevlogDate } from "./devlogTime";
import {
  createDevlogPost,
  deleteDevlogPost,
  listDevlogPosts,
  updateDevlogPost,
  type DevlogPost,
} from "./api";

type EditorState = {
  postId: string | null;
  title: string;
  body: string;
};

const EMPTY_EDITOR: EditorState = {
  postId: null,
  title: "",
  body: "",
};

const PUBLIC_DEVLOG_AUTHOR = "CataDragon嘎嘎龙";

function postWasUpdated(post: DevlogPost) {
  return post.updated_at !== post.created_at;
}

export function DevlogPage() {
  const { copy, language } = useI18n();
  const labels = copy.devlog;
  const [posts, setPosts] = useState<DevlogPost[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [editor, setEditor] = useState<EditorState>(EMPTY_EDITOR);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteCandidateId, setDeleteCandidateId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void listDevlogPosts()
      .then((page) => {
        if (cancelled) {
          return;
        }
        setPosts(page.items);
        setNextCursor(page.next_cursor ?? null);
        setIsLoading(false);
      })
      .catch((loadError) => {
        if (cancelled) {
          return;
        }
        setError(loadError instanceof Error ? loadError.message : labels.failed);
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [labels.failed]);

  useEffect(() => {
    const token = getStoredSessionToken();
    if (!token) {
      setIsAdmin(false);
      return;
    }

    let cancelled = false;
    void getOwnProfile(token)
      .then((profile) => {
        if (!cancelled) {
          setIsAdmin(profile.admin_or_not);
        }
      })
      .catch((adminError) => {
        if (cancelled) {
          return;
        }
        if (adminError instanceof ApiError && adminError.status === 401) {
          setIsAdmin(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const latestPost = posts[0] ?? null;
  const heroStat = useMemo(() => {
    if (!latestPost) {
      return labels.noPosts;
    }
    return formatDevlogDate(latestPost.created_at, language);
  }, [labels.noPosts, language, latestPost]);
  const heroClock = latestPost ? formatDevlogClock(latestPost.created_at, language) : null;

  const loadMore = async () => {
    if (!nextCursor || isLoadingMore) {
      return;
    }
    setIsLoadingMore(true);
    setError(null);
    try {
      const page = await listDevlogPosts(nextCursor);
      setPosts((current) => [...current, ...page.items]);
      setNextCursor(page.next_cursor ?? null);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : labels.failed);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const resetEditor = () => {
    setEditor(EMPTY_EDITOR);
    setError(null);
  };

  const submitEditor = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const token = getStoredSessionToken();
    if (!token) {
      setError(labels.adminOnly);
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      const payload = {
        title: editor.title,
        body: editor.body,
      };
      const saved = editor.postId
        ? await updateDevlogPost(token, editor.postId, payload)
        : await createDevlogPost(token, payload);
      setPosts((current) => {
        const exists = current.some((post) => post.post_id === saved.post_id);
        if (exists) {
          return current.map((post) => (post.post_id === saved.post_id ? saved : post));
        }
        return [saved, ...current];
      });
      setEditor(EMPTY_EDITOR);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : labels.saveFailed);
    } finally {
      setIsSaving(false);
    }
  };

  const beginEdit = (post: DevlogPost) => {
    setEditor({
      postId: post.post_id,
      title: post.title,
      body: post.body,
    });
    setDeleteCandidateId(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const confirmDelete = async (postId: string) => {
    const token = getStoredSessionToken();
    if (!token) {
      setError(labels.adminOnly);
      return;
    }
    setError(null);
    try {
      await deleteDevlogPost(token, postId);
      setPosts((current) => current.filter((post) => post.post_id !== postId));
      setDeleteCandidateId(null);
      if (editor.postId === postId) {
        setEditor(EMPTY_EDITOR);
      }
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : labels.deleteFailed);
    }
  };

  return (
    <div className="page-stack devlog-page">
      <section className="devlog-hero">
        <article className="devlog-hero__copy">
          <h1>{labels.title}</h1>
        </article>
        <aside className="devlog-status-card" aria-label={labels.statusLabel}>
          <span>{labels.latest}</span>
          <strong>{heroStat}</strong>
          {heroClock ? <small>{heroClock}</small> : null}
          {isAdmin ? <p>{labels.adminMode}</p> : null}
        </aside>
      </section>

      {isAdmin ? (
        <section className="devlog-editor" aria-label={labels.editorLabel}>
          <div className="devlog-editor__head">
            <div>
              <p className="section-card__eyebrow">{labels.editorEyebrow}</p>
              <h2>{editor.postId ? labels.editing : labels.newPost}</h2>
            </div>
            {editor.postId ? (
              <button className="devlog-text-button" type="button" onClick={resetEditor}>
                {labels.cancelEdit}
              </button>
            ) : null}
          </div>
          <form className="devlog-editor__form" onSubmit={submitEditor}>
            <div className="field">
              <label htmlFor="devlog-title">{labels.titleLabel}</label>
              <input
                id="devlog-title"
                type="text"
                maxLength={140}
                value={editor.title}
                onChange={(event) =>
                  setEditor((current) => ({ ...current, title: event.target.value }))
                }
              />
            </div>
            <div className="field">
              <label htmlFor="devlog-body">{labels.bodyLabel}</label>
              <textarea
                id="devlog-body"
                value={editor.body}
                onChange={(event) =>
                  setEditor((current) => ({ ...current, body: event.target.value }))
                }
                placeholder={labels.bodyPlaceholder}
              />
            </div>
            <div className="devlog-editor__actions">
              <button className="button-link button-link--secondary" type="button" onClick={resetEditor}>
                {labels.clear}
              </button>
              <button className="button-link button-link--primary" type="submit" disabled={isSaving}>
                {isSaving ? labels.saving : editor.postId ? labels.save : labels.publish}
              </button>
            </div>
          </form>
        </section>
      ) : null}

      {error ? <p className="devlog-error" role="alert">{error}</p> : null}

      <section className="devlog-feed" aria-label={labels.feedLabel}>
        {isLoading ? (
          <article className="devlog-empty">{labels.loading}</article>
        ) : posts.length === 0 ? (
          <article className="devlog-empty">{labels.empty}</article>
        ) : (
          posts.map((post) => (
            <article className="devlog-post" key={post.post_id}>
              <div className="devlog-post__date">
                <time dateTime={post.created_at}>{formatDevlogDate(post.created_at, language)}</time>
                <time className="devlog-post__clock" dateTime={post.created_at}>
                  {formatDevlogClock(post.created_at, language)}
                </time>
                <span>@{PUBLIC_DEVLOG_AUTHOR}</span>
              </div>
              <div className="devlog-post__content">
                <div className="devlog-post__head">
                  <h2>{post.title}</h2>
                  {postWasUpdated(post) ? <span>{labels.updated}</span> : null}
                </div>
                <p className="devlog-post__body">{post.body}</p>
                {isAdmin ? (
                  <div className="devlog-post__actions">
                    <button type="button" onClick={() => beginEdit(post)}>
                      {labels.edit}
                    </button>
                    {deleteCandidateId === post.post_id ? (
                      <>
                        <button
                          className="devlog-post__delete-confirm"
                          type="button"
                          onClick={() => void confirmDelete(post.post_id)}
                        >
                          {labels.confirmDelete}
                        </button>
                        <button type="button" onClick={() => setDeleteCandidateId(null)}>
                          {labels.cancel}
                        </button>
                      </>
                    ) : (
                      <button type="button" onClick={() => setDeleteCandidateId(post.post_id)}>
                        {labels.delete}
                      </button>
                    )}
                  </div>
                ) : null}
              </div>
            </article>
          ))
        )}
      </section>

      {nextCursor ? (
        <button className="devlog-more" type="button" onClick={() => void loadMore()} disabled={isLoadingMore}>
          {isLoadingMore ? labels.loadingMore : labels.loadOlder}
        </button>
      ) : null}
    </div>
  );
}
